import React from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import { Modal } from 'components/Modal/Modal.component';
import { Col, Row } from 'components/layout';

import { REGEX } from 'lib/utils/regex';

import styles from './AddOrEditLinkModal.module.scss';

interface AddOrEditLinkModalProps {
  isEdit: boolean;
  saveLink: (title: string, link: string, index?: number) => void;
  index?: number;
  deleteLink?: (index: number) => void;
  title?: string;
  link?: string;
  onClose: () => void;
}

export const AddOrEditLinkModal: React.FC<AddOrEditLinkModalProps> = ({
  isEdit,
  title: titleProp,
  link: linkProp,
  onClose: onCloseProp,
  saveLink,
  deleteLink,
  index: linkIndex,
}) => {
  const [title, setTitle] = React.useState<string>(titleProp || '');
  const [link, setLink] = React.useState<string>(linkProp || '');
  const [t] = useTranslation('onboarding.manager');

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const onClose = () => {
    setTitle('');
    setLink('');
    setErrors({});
    onCloseProp();
  };

  const onTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
    setErrors({ ...errors, title: '' });
  };

  const onLinkChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLink(event.target.value);
    setErrors({ ...errors, link: '' });
  };

  const onSave = () => {
    if (!title) {
      setErrors({ ...errors, title: t('step-2.link-modal.title.required') });
      return;
    }
    if (!link) {
      setErrors({ ...errors, link: t('step-2.link-modal.link.required') });
      return;
    }
    if (!REGEX.URL.test(link)) {
      setErrors({ ...errors, link: t('step-2.link-modal.link.invalid') });
      return;
    }
    if (isEdit && linkIndex !== undefined) {
      saveLink(title, link, linkIndex);
    } else {
      saveLink(title, link);
    }
    onClose();
  };

  const onDelete = () => {
    if (deleteLink) {
      deleteLink(linkIndex || 0);
    }
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={true}
        onRequestClose={onClose}
        contentClassName={styles.ProfileModal}
      >
        <div className={styles.ProfileModalHeader}>
          {isEdit ? (
            <p>{t('step-2.link-modal.edit')}</p>
          ) : (
            <p>{t('step-2.link-modal.add')}</p>
          )}
          <IconButton
            onClick={onClose}
            className={styles.ProfileModalHeaderClose}
            iconName="close"
            size="small"
          />
        </div>
        <Row className={styles.ProfileModalContent}>
          <Col span={24} className={styles.inputCol}>
            <InputV2
              label={t('step-2.link-modal.title.label')}
              value={title}
              onChange={onTitleChange}
              placeholder={t('step-2.link-modal.title.placeholder')}
              labelClassName={styles.fieldLabel}
              isInvalid={!!errors.title}
            />
            {errors.title && (
              <div className={styles.errorMessage}>{errors.title}</div>
            )}
          </Col>
          <Col span={24} className={styles.inputCol}>
            <InputV2
              label={t('step-2.link-modal.link.label')}
              value={link}
              onChange={onLinkChange}
              placeholder={t('step-2.link-modal.link.placeholder')}
              labelClassName={styles.fieldLabel}
              isInvalid={!!errors.link}
            />
            {errors.link && (
              <div className={styles.errorMessage}>{errors.link}</div>
            )}
          </Col>
        </Row>
        <div className={styles.ProfileModalFooter}>
          {isEdit && (
            <ButtonV3
              className={styles.cancelButton}
              onClick={onDelete}
              color="secondary"
            >
              {t('step-2.link-modal.delete')}
            </ButtonV3>
          )}
          <ButtonV3
            className={styles.saveButton}
            onClick={onSave}
            color="primary"
          >
            {t('step-2.link-modal.save')}
          </ButtonV3>
        </div>
      </Modal>
    </>
  );
};
